package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.AnimationAst
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.LValueAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

/**
 * 引用解析阶段。
 *
 * 检查内容：
 * - action 体内的 [StmtAst.AssignStmt]：lhs/rhs 中的 state 引用是否声明；rhs 中的 Call 是否声明
 * - root 元素树中的属性表达式：state 引用 / Call 引用
 * - 事件 prop（见 [EventProps.NAMES]，如 `onClick`、`onChange`）必须绑定一个无参事件入口
 */
class ReferenceResolver(
    private val diag: DiagnosticCollector,
    private val scope: Scope,
    /** 已知 theme token 白名单；为空时跳过 theme.x 校验（默认行为，零回归）。 */
    private val knownThemeTokens: Set<String> = emptySet(),
    /** 严格模式：true 时未知 theme.x 报 ERROR，false 时报 WARNING。 */
    private val strictThemeTokens: Boolean = false,
) {

    fun resolve(screen: ScreenAst) {
        // C4：screen 级 fun 体也需要做引用解析；其中对其它 screen-level fun 的调用
        // 统一通过 CallExpr(FunRefExpr) 表达，无需额外校验目标存在
        screen.funs.forEach { fn ->
            fn.body.forEach(::walkStmt)
        }

        screen.animations.forEach { animation ->
            walkAnimation(animation.node)
        }

        // derived 派生值表达式：校验其中引用的 state / derived 名是否已声明
        screen.derived?.entries?.forEach { entry ->
            walkExpr(entry.expr)
        }

        // 元素树属性
        walkElement(screen.root)
    }

    private fun walkStmt(stmt: StmtAst) {
        when (stmt) {
            is StmtAst.AssignStmt -> {
                when (val lhs = stmt.lhs) {
                    is LValueAst.StateLValue -> checkStateName(lhs.name, memberNamePosition(lhs.position, lhs.name))
                    // 局部赋值在 parser 已校验作用域，这里仅递归 rhs
                    is LValueAst.LocalLValue -> Unit
                    // session / persist 字段不在编译期单文件诊断（跨 screen 共享，A 写 B 读）
                    is LValueAst.SessionLValue, is LValueAst.PersistLValue -> Unit
                    // M6-a：索引位置赋值，target/index 都按表达式递归
                    is LValueAst.IndexLValue -> {
                        walkExpr(lhs.target)
                        walkExpr(lhs.index)
                    }
                }
                walkExpr(stmt.rhs)
            }
            is StmtAst.ExprStmt -> walkExpr(stmt.call)
            is StmtAst.VarDeclStmt -> walkExpr(stmt.initializer)
            is StmtAst.IfStmt -> {
                walkExpr(stmt.cond)
                stmt.thenBody.forEach(::walkStmt)
                stmt.elseBody.forEach(::walkStmt)
            }
            is StmtAst.WhileStmt -> {
                walkExpr(stmt.cond)
                stmt.body.forEach(::walkStmt)
            }
            is StmtAst.ReturnStmt -> stmt.value?.let(::walkExpr)
            is StmtAst.TryStmt -> {
                stmt.tryBody.forEach(::walkStmt)
                stmt.catchBody.forEach(::walkStmt)
                stmt.finallyBody.forEach(::walkStmt)
            }
            // batch 语句：递归 walkStmt body（与 TryStmt 的 tryBody 处理一致）
            is StmtAst.BatchStmt -> stmt.body.forEach(::walkStmt)
            is StmtAst.ForInStmt -> {
                walkExpr(stmt.iterable)
                stmt.body.forEach(::walkStmt)
            }
            is StmtAst.WhenStmt -> walkWhen(stmt.whenExpr)
            is StmtAst.FunDeclStmt -> stmt.decl.body.forEach(::walkStmt)
            is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> Unit
        }
    }

    private fun walkWhen(w: ExprAst.WhenExpr) {
        walkExpr(w.subject)
        w.branches.forEach { br ->
            br.values.forEach(::walkExpr)
            br.body.forEach(::walkStmt)
        }
        w.elseBody?.forEach(::walkStmt)
    }

    private fun isOverlayAnimationPreset(prop: ElementAst.PropAssign): Boolean {
        if (prop.name != "animation") return false
        val value = (prop.value as? ExprAst.Literal.StringLit)?.value ?: return false
        return value in OVERLAY_ANIMATION_PRESETS
    }

    private fun walkElement(element: ElementAst) {
        element.props.forEach { prop ->
            // 特殊：事件 prop（见 EventProps.NAMES）必须是无参事件入口
            val propName = prop.name
            if (propName in EventProps.NAMES) {
                checkEventBinding(propName, prop.value)
            } else if (propName in AnimationRefs.NAMES && !isOverlayAnimationPreset(prop)) {
                val animationId = (prop.value as? ExprAst.Literal.StringLit)?.value
                if (animationId == null) {
                    diag.error(
                        DslError.E302_UNKNOWN_PROP,
                        prop.value.position,
                        "$propName 必须引用一个字符串 animation id",
                    )
                } else {
                    checkAnimationName(animationId, prop.value.position)
                }
            } else {
                // M6-f：字面量级类型校验（表达式放过到 runtime）
                checkM6fLiteralProp(propName, prop.value)
                walkExpr(prop.value)
            }
        }
        element.modifiers.forEach { modifier ->
            modifier.fields.forEach { field -> walkExpr(field.value) }
        }
        // RFC-RECOMPOSITION：conditional 包装节点 children 永远空，但分支体内的元素也要 walk。
        // 同时 conditional 的 subject 与 branch.condition 表达式自身也包含引用（如 state.x、
        // slot.read("id") / fun 调用），必须走 walkExpr 才能完整校验。
        element.conditionalContent?.let { content ->
            content.subject?.let(::walkExpr)
            for (branch in content.branches) {
                branch.condition?.let(::walkExpr)
                // 注意：branch.values 全是字面量（parser 阶段保证），无需走 walkExpr
            }
        }
        // $for：iterable 表达式自身也含引用（state.x / slot.read / fun 调用），需 walkExpr。
        // 循环变量在 body 内通过 parser 升格为 IdentRef；IdentRef 不做未声明校验（仅丢弃名 `_` 报错），
        // 故 body 内 item/index/k/v 裸标识符天然被容忍。
        element.forContent?.let { walkExpr(it.iterable) }
        top.wcpe.allincore.dsl.compiler.ast.ElementWalk.directDescendants(element).forEach(::walkElement)
    }

    /**
     * M6-f §8.7：字面量类型守护。
     * - enabled / visible：必须为 Boolean 字面量（E742/E743）
     * - paddingTop/Right/Bottom/Left：必须为 Number 字面量（E744）
     */
    private fun checkM6fLiteralProp(propName: String, value: ExprAst) {
        when (propName) {
            "enabled" -> if (value is ExprAst.Literal && value !is ExprAst.Literal.BoolLit) {
                diag.error(DslError.E768_ENABLED_TYPE_MISMATCH, value.position, literalKind(value))
            }
            "visible" -> if (value is ExprAst.Literal && value !is ExprAst.Literal.BoolLit) {
                diag.error(DslError.E743_VISIBLE_TYPE_MISMATCH, value.position, literalKind(value))
            }
            "paddingTop", "paddingRight", "paddingBottom", "paddingLeft" ->
                if (value is ExprAst.Literal && value !is ExprAst.Literal.NumberLit) {
                    diag.error(
                        DslError.E744_PADDING_SIDE_TYPE_MISMATCH,
                        value.position,
                        propName,
                        literalKind(value),
                    )
                }
        }
    }

    private fun literalKind(value: ExprAst.Literal): String = when (value) {
        is ExprAst.Literal.StringLit -> "String"
        is ExprAst.Literal.NumberLit -> "Number"
        is ExprAst.Literal.BoolLit -> "Boolean"
    }

    /**
     * M6-h Round 2：toast(message, kind="info", duration=3000, position="top") 字面量取值校验。
     * 字符串/数字字面量直接命中 E752 / E753 / E754；非字面量表达式留给 runtime 处理。
     */
    private fun checkToastBuiltinLiterals(expr: ExprAst.BuiltinCallExpr) {
        // 位置参数：args[1]=kind / args[2]=duration / args[3]=position
        if (expr.args.size > 1) checkToastKind(expr.args[1])
        if (expr.args.size > 2) checkToastDuration(expr.args[2])
        if (expr.args.size > 3) checkToastPosition(expr.args[3])
        for (na in expr.namedArgs) {
            when (na.name) {
                "kind" -> checkToastKind(na.value)
                "duration" -> checkToastDuration(na.value)
                "position" -> checkToastPosition(na.value)
            }
        }
    }

    private fun checkToastKind(value: ExprAst) {
        val v = value as? ExprAst.Literal.StringLit ?: return
        if (v.value !in TOAST_KIND_VALUES) {
            diag.error(DslError.E752_TOAST_KIND_UNKNOWN, v.position, v.value)
        }
    }

    private fun checkToastPosition(value: ExprAst) {
        val v = value as? ExprAst.Literal.StringLit ?: return
        if (v.value !in TOAST_POSITION_VALUES) {
            diag.error(DslError.E753_TOAST_POSITION_UNKNOWN, v.position, v.value)
        }
    }

    private fun checkToastDuration(value: ExprAst) {
        val lit = value as? ExprAst.Literal ?: return
        val invalid = when (lit) {
            is ExprAst.Literal.NumberLit -> lit.value.toDouble() < 0.0
            else -> true
        }
        if (invalid) {
            diag.error(DslError.E754_TOAST_DURATION_INVALID, lit.position, literalKind(lit))
        }
    }

    /**
     * M6-i：animate(name, target?, onComplete?) 字面量校验。
     *
     * - name 是 [ExprAst.Literal.StringLit] 时，校验该名在 screen 的 animation 声明中存在；
     *   否则报 [DslError.E761_ANIMATE_NAME_NOT_FOUND]。
     * - 非字面量 name / target 留 runtime 处理（E762 在 runtime 抛 DslRuntimeError）。
     */
    private fun checkAnimateBuiltinLiterals(expr: ExprAst.BuiltinCallExpr) {
        val nameExpr = expr.args.firstOrNull() ?: expr.namedArgs.firstOrNull { it.name == "name" }?.value
        ?: return
        val nameLit = nameExpr as? ExprAst.Literal.StringLit ?: return
        if (nameLit.value !in scope.animationNames) {
            diag.error(DslError.E761_ANIMATE_NAME_NOT_FOUND, nameLit.position, nameLit.value)
        }
    }

    private fun checkEventBinding(propName: String, expr: ExprAst) {
        when (expr) {
            // M6-e 集成：事件 prop 接受 `::fun` 与 `::fun(args)` 两种形态。
            // 后者由 boundArgs / boundNamedArgs 抵消 fun 形参，剩余形参数应为 0。
            is ExprAst.FunRefExpr -> {
                val boundCount = expr.boundArgs.size + expr.boundNamedArgs.size
                checkFunEventEntry(propName, expr.name, expr.position, boundCount)
            }
            is ExprAst.CallExpr -> {
                val callee = expr.callee
                if (callee is ExprAst.FunRefExpr && expr.args.isEmpty()) {
                    val boundCount = callee.boundArgs.size + callee.boundNamedArgs.size
                    checkFunEventEntry(propName, callee.name, callee.position, boundCount)
                } else if (callee is ExprAst.IdentRef && expr.args.isEmpty()) {
                    checkEventEntryName(callee.name, callee.position)
                } else {
                    diag.error(
                        DslError.E301_LITERAL_TYPE_MISMATCH,
                        expr.position,
                        "$propName 必须绑定一个无参 fun 调用、::fun 引用，或形参为 1 的 lambda",
                    )
                }
            }
            // RFC-INPUT-EVENT-UNIFICATION Stage 2：允许 1 参 lambda 字面量作为事件 prop 右值。
            // 形参名建议 `event`，但不强制；lambda 体内可通过形参访问 event Map 字段。
            // 多于 / 少于 1 个形参在此处统一报 DSL_E301，错误信息说明期望形参数。
            is ExprAst.LambdaExpr -> {
                if (expr.params.size != 1) {
                    diag.error(
                        DslError.E301_LITERAL_TYPE_MISMATCH,
                        expr.position,
                        "$propName lambda 必须接收恰好 1 个形参（建议命名为 'event'），实际 ${expr.params.size} 个",
                    )
                } else {
                    // 复用普通 lambda 体的引用检查（state / 内建调用 / 嵌套表达式）
                    expr.body.forEach(::walkStmt)
                }
            }
            else -> {
                diag.error(
                    DslError.E301_LITERAL_TYPE_MISMATCH,
                    expr.position,
                    "$propName 必须绑定一个无参 fun 调用、::fun 引用，或形参为 1 的 lambda",
                )
            }
        }
    }

    private fun walkAnimation(node: AnimationAst.Node) {
        checkNonNegative(node.durationMs, "duration", node.durationPosition ?: node.position)
        checkNonNegative(node.delayMs, "delay", node.delayPosition ?: node.position)
        checkNonNegative(node.staggerMs, "stagger", node.staggerPosition ?: node.position)
        node.easing?.let { easing ->
            if (easing !in ALLOWED_EASINGS) {
                diag.error(
                    DslError.E502_PROP_APPLY_FAILED,
                    node.easingPosition ?: node.position,
                    easing,
                )
            }
        }
        // 计划 07 M3：easing 与 spring 互斥；同时声明视为驱动方式冲突。
        node.spring?.let { spring ->
            if (node.easing != null) {
                diag.error(
                    DslError.E503_ANIMATION_EASING_SPRING_CONFLICT,
                    spring.position,
                )
            }
            checkSpringParams(spring)
        }

        node.props.forEach(::checkAnimationPropLiteral)
        node.keyframes.forEach { keyframe ->
            if (keyframe.offset < 0.0 || keyframe.offset > 1.0) {
                diag.error(
                    DslError.E501_PROP_NORMALIZE_FAILED,
                    keyframe.position,
                    "keyframe offset 必须在 0..1",
                )
            }
            keyframe.props.forEach(::checkAnimationPropLiteral)
        }
        node.children.forEach(::walkAnimation)
    }

    private fun checkAnimationPropLiteral(prop: AnimationAst.PropAssign) {
        if (prop.value !is ExprAst.Literal) {
            diag.error(
                DslError.E303_RECURSION_DEPTH_EXCEEDED,
                prop.value.position,
                "动画属性 ${prop.name} 必须是字面量",
            )
        }
    }

    private fun walkExpr(expr: ExprAst) {
        when (expr) {
            is ExprAst.StateRef -> checkStateName(expr.name, memberNamePosition(expr.position, expr.name))
            // session / persist 字段不要求声明，跨 screen 共享；编译期不做单文件校验
            is ExprAst.SessionRef, is ExprAst.PersistRef -> Unit
            is ExprAst.DerivedRef -> checkDerivedName(expr.name, memberNamePosition(expr.position, expr.name))
            is ExprAst.Interp -> expr.parts.forEach { part ->
                if (part is ExprAst.Interp.InterpPart.ExprPart) walkExpr(part.expr)
            }
            is ExprAst.BinaryOp -> {
                walkExpr(expr.left)
                walkExpr(expr.right)
            }
            is ExprAst.MemberAccess -> {
                val paramsTarget = expr.target as? ExprAst.IdentRef
                if (paramsTarget?.name == "params") {
                    checkParamName(expr.member, memberNamePosition(expr.position, expr.member))
                } else {
                    walkExpr(expr.target)
                }
            }
            is ExprAst.MemberCall -> {
                // M6-f §8.4：toFixed(digits) 字面量参数若非 NumberLit → E746
                if (expr.method == "toFixed" && expr.args.size == 1) {
                    val arg = expr.args[0]
                    if (arg is ExprAst.Literal && arg !is ExprAst.Literal.NumberLit) {
                        diag.error(
                            DslError.E746_TO_FIXED_ARG_NOT_NUMBER,
                            arg.position,
                            literalKind(arg),
                        )
                    }
                }
                walkExpr(expr.target)
                expr.args.forEach(::walkExpr)
                expr.namedArgs.forEach { walkExpr(it.value) }
            }
            is ExprAst.SafeMemberAccess -> walkExpr(expr.target)
            // M10 Stage 6：`target!!` 透传 target
            is ExprAst.NonNullAssert -> walkExpr(expr.target)
            is ExprAst.SafeMemberCall -> {
                walkExpr(expr.target)
                expr.args.forEach(::walkExpr)
            }
            is ExprAst.UnaryOp -> walkExpr(expr.operand)
            is ExprAst.Elvis -> {
                walkExpr(expr.left)
                walkExpr(expr.right)
            }
            is ExprAst.Ternary -> {
                walkExpr(expr.cond)
                walkExpr(expr.thenExpr)
                walkExpr(expr.elseExpr)
            }
            is ExprAst.IfExpr -> {
                walkExpr(expr.cond)
                expr.thenBody.forEach(::walkStmt)
                expr.elseBody.forEach(::walkStmt)
            }
            // LocalRef 名称解析由 parser 在解析期间通过 Parser.localStack 保证；语义层无需重复校验
            // 但 `_` 是丢弃占位符，禁止在 body 内被引用（E611）。
            is ExprAst.LocalRef -> if (expr.name == DISCARD_NAME) {
                diag.error(DslError.E611_DISCARD_PARAM_REFERENCED, expr.position)
            }
            // PropsRef 仅在 component root 内出现，展开阶段替换；语义层不校验
            is ExprAst.PropsRef -> Unit
            is ExprAst.NullLit -> Unit
            is ExprAst.IdentRef -> if (expr.name == DISCARD_NAME) {
                diag.error(DslError.E611_DISCARD_PARAM_REFERENCED, expr.position)
            }
            is ExprAst.ThemeRef ->
                // 白名单非空时才校验：未定义的 theme.x 在严格模式报 ERROR，否则报 WARNING。
                if (knownThemeTokens.isNotEmpty() && expr.key !in knownThemeTokens) {
                    if (strictThemeTokens) {
                        diag.error(DslError.E796_UNKNOWN_THEME_TOKEN, memberNamePosition(expr.position, expr.key), expr.key)
                    } else {
                        diag.warning(DslError.E796_UNKNOWN_THEME_TOKEN, memberNamePosition(expr.position, expr.key), expr.key)
                    }
                }
            is ExprAst.Literal -> Unit
            is ExprAst.ListLiteralExpr -> expr.elements.forEach(::walkExpr)
            is ExprAst.MapLiteralExpr -> expr.entries.forEach { walkExpr(it.value) }
            is ExprAst.RangeExpr -> {
                walkExpr(expr.start)
                walkExpr(expr.endValue)
            }
            is ExprAst.IndexExpr -> {
                walkExpr(expr.target)
                walkExpr(expr.index)
            }
            is ExprAst.WhenExpr -> walkWhen(expr)
            is ExprAst.LambdaExpr -> expr.body.forEach(::walkStmt)
            // ::name 函数引用：C3 仅作语法引用对象，不强制校验目标存在。
            is ExprAst.FunRefExpr -> Unit
            is ExprAst.CallExpr -> {
                walkExpr(expr.callee)
                expr.args.forEach(::walkExpr)
            }
            // C5：net.request(...) 单回调形态。payload 与 callback 都需走 walkExpr。
            is ExprAst.NetRequestCall -> {
                expr.payload.forEach { walkExpr(it.value) }
                expr.callback?.let(::walkExpr)
                expr.timeoutMs?.let(::walkExpr)
            }
            // C6：await 包裹的 net.request / suspend lambda 调用走同样的子表达式遍历
            is ExprAst.AwaitExpr -> {
                expr.netRequestCall?.let(::walkExpr)
                expr.suspendCall?.let(::walkExpr)
                expr.tailExpr?.let(::walkExpr)
            }
            is ExprAst.BuiltinCallExpr -> checkBuiltinCall(expr)
            is ExprAst.IsExpr -> walkExpr(expr.target)
            // Stage B：net.subscribe("ns.topic", handler) —— 走专用校验入口
            is ExprAst.NetSubscribeCall -> checkNetSubscribeCall(expr)
        }
    }

    /**
     * 内建调用单一入口：先做字面量级语义校验（format/toFixed/toast/animate），
     * 再递归子表达式。从 [walkExpr] 抽出，缩短主分发方法。
     */
    private fun checkBuiltinCall(expr: ExprAst.BuiltinCallExpr) {
        when (expr.name) {
            "format" -> checkFormatFirstArgLiteral(expr)
            "toast" -> checkToastBuiltinLiterals(expr)
            "animate" -> checkAnimateBuiltinLiterals(expr)
        }
        expr.args.forEach(::walkExpr)
        expr.namedArgs.forEach { walkExpr(it.value) }
    }

    /** M6-f §8.4：format(fmt, ...) 第一参数若为字面量但非 StringLit，报 E745。 */
    private fun checkFormatFirstArgLiteral(expr: ExprAst.BuiltinCallExpr) {
        val first = expr.args.firstOrNull() ?: return
        if (first is ExprAst.Literal && first !is ExprAst.Literal.StringLit) {
            diag.error(
                DslError.E745_FORMAT_FIRST_ARG_NOT_STRING,
                first.position,
                literalKind(first),
            )
        }
    }

    /** 把完整引用范围收窄到末尾成员名，诊断不再覆盖 `state.` 等限定前缀。 */
    private fun memberNamePosition(
        position: top.wcpe.allincore.dsl.compiler.source.SourcePosition,
        name: String,
    ): top.wcpe.allincore.dsl.compiler.source.SourcePosition {
        val range = position.range
        if (range == top.wcpe.allincore.dsl.compiler.source.SourceRange.NONE || name.isEmpty() || name.length > range.length) return position
        val start = range.end - name.length
        return top.wcpe.allincore.dsl.compiler.source.SourcePosition(
            line = position.line,
            column = position.column + (start - range.start),
            offset = start,
            sourceId = range.sourceId,
            endOffset = range.end,
        )
    }

    private fun checkStateName(name: String, position: top.wcpe.allincore.dsl.compiler.source.SourcePosition) {
        if (name !in scope.stateNames) {
            diag.error(DslError.E201_UNDECLARED_STATE, position, name)
        }
    }

    private fun checkParamName(name: String, position: top.wcpe.allincore.dsl.compiler.source.SourcePosition) {
        if (name !in scope.paramNames) {
            diag.error(DslError.AUI_V2_UNDECLARED_PARAM, position, name)
        }
    }

    private fun checkDerivedName(name: String, position: top.wcpe.allincore.dsl.compiler.source.SourcePosition) {
        if (name !in scope.derivedNames) {
            diag.error(DslError.E201_UNDECLARED_STATE, position, "derived.$name")
        }
    }

    private fun checkEventEntryName(name: String, position: top.wcpe.allincore.dsl.compiler.source.SourcePosition) {
        if (name in scope.funNames) {
            checkFunEventEntry("事件", name, position, boundCount = 0)
            return
        }
        diag.error(DslError.E202_UNDECLARED_ACTION, position, "未声明的事件入口 '$name'")
    }

    /**
     * 校验事件 prop 引用的 fun：
     * - fun 必须存在；
     * - fun 形参数减去 [boundCount]（M6-e `::fun(args)` 部分应用绑定的实参数）后应为 0，
     *   否则报 E301。
     */
    private fun checkFunEventEntry(
        propName: String,
        name: String,
        position: top.wcpe.allincore.dsl.compiler.source.SourcePosition,
        boundCount: Int,
    ) {
        val paramCount = scope.funParamCounts[name]
        if (paramCount == null) {
            diag.error(DslError.E202_UNDECLARED_ACTION, position, "未声明的事件入口 '$name'")
            return
        }
        if (paramCount - boundCount != 0) {
            diag.error(
                DslError.E301_LITERAL_TYPE_MISMATCH,
                position,
                "$propName 必须绑定无参 fun '$name'",
            )
        }
    }

    private fun checkAnimationName(name: String, position: top.wcpe.allincore.dsl.compiler.source.SourcePosition) {
        if (name !in scope.animationNames) {
            diag.error(DslError.E203_UNDECLARED_ANIMATION, position, name)
        }
    }

    // ===== Stage B：net.subscribe 参数形态校验 =====

    /** 标识符合法性正则：Java 标识符规则。 */
    private val identifierPattern = Regex("^[A-Za-z_][A-Za-z0-9_]*$")

    /**
     * 校验 `net.subscribe("namespace.topic", handler)` 参数：
     * - namespace / topic 命名合法性（parser 已从字面量拆出，必校验）
     * - handler 必须是 FunRefExpr 或 1 参 LambdaExpr，否则报 E784
     */
    private fun checkNetSubscribeCall(expr: ExprAst.NetSubscribeCall) {
        if (!identifierPattern.matches(expr.namespace)) {
            diag.error(DslError.E781_SUBSCRIBE_NAMESPACE_INVALID, expr.position, expr.namespace)
        }
        if (!identifierPattern.matches(expr.topic)) {
            diag.error(DslError.E782_SUBSCRIBE_TOPIC_INVALID, expr.position, expr.topic)
        }
        val handler = expr.handler
        if (handler !is ExprAst.FunRefExpr && !isOneLambda(handler)) {
            diag.error(DslError.E784_SUBSCRIBE_HANDLER_INVALID, handler.position)
        }
        // 继续递归 handler 子节点（lambda body 内可能含其它需要校验的表达式）
        walkExpr(handler)
    }

    /** 判断表达式是否为 1 参 lambda。 */
    private fun isOneLambda(expr: ExprAst): Boolean =
        expr is ExprAst.LambdaExpr && expr.params.size == 1

    private fun checkNonNegative(value: Double?, field: String, position: top.wcpe.allincore.dsl.compiler.source.SourcePosition) {
        if (value != null && value < 0.0) {
            diag.error(DslError.E501_PROP_NORMALIZE_FAILED, position, "动画 $field 必须非负")
        }
    }

    /** 校验 spring 参数：stiffness / mass 必须为正，damping 必须非负。缺省参数（null）走默认值，跳过校验。 */
    private fun checkSpringParams(spring: AnimationAst.SpringAst) {
        spring.stiffness?.let {
            if (it <= 0.0) {
                diag.error(DslError.E504_ANIMATION_SPRING_PARAM_INVALID, spring.position, "stiffness", "必须为正数")
            }
        }
        spring.mass?.let {
            if (it <= 0.0) {
                diag.error(DslError.E504_ANIMATION_SPRING_PARAM_INVALID, spring.position, "mass", "必须为正数")
            }
        }
        spring.damping?.let {
            if (it < 0.0) {
                diag.error(DslError.E504_ANIMATION_SPRING_PARAM_INVALID, spring.position, "damping", "必须非负")
            }
        }
    }

    companion object {
        private val OVERLAY_ANIMATION_PRESETS = setOf("none", "fade", "scale", "fade-scale")

        // 与 Easing.fromName 接受的名字保持一致；保留旧短名 + 12 种 Material/Cubic/Back/Bounce/Elastic。
        private val ALLOWED_EASINGS = setOf(
            "linear",
            "easeIn", "easeOut", "easeInOut",
            "easeInQuad", "easeOutQuad", "easeInOutQuad",
            "easeInCubic", "easeOutCubic", "easeInOutCubic",
            "easeInBack", "easeOutBack",
            "easeOutBounce",
            "easeOutElastic",
        )
        /** 丢弃占位符形参名。 */
        private const val DISCARD_NAME = "_"

        /** M6-h Round 2：toast kind 合法字面量取值。 */
        private val TOAST_KIND_VALUES = setOf("info", "success", "warning", "error")

        /** M6-h Round 2：toast position 合法字面量取值。 */
        private val TOAST_POSITION_VALUES = setOf("top", "bottom", "center")
    }
}
